import { useState } from 'react';
import { Pencil, Plus, Trash2, Users } from 'lucide-react';
import type { SavedReply } from '@peles/shared';
import { EntityList, type EntityListColumn } from '../common';
import { useDialog } from '../../context/DialogContext';
import { useDeleteSavedReply, useSavedReplies } from '../../hooks/useSavedReplies';
import { SavedReplyModal } from './SavedReplyModal';

/**
 * Saved replies, on the Profile page: the writer's own and the office's shared
 * ones, with add, edit and delete.
 *
 * On Profile rather than in Settings because a saved reply is a person's own
 * tool, like their notification settings beside it — Settings is the
 * administrator's, and a producer who cannot open it still needs to tidy
 * the replies they saved from a composer. Shared replies somebody else wrote
 * are listed (they are offered in every composer) but only their author or
 * an administrator can change them, which the server decides as `canEdit`.
 */
export function SavedRepliesSection() {
  const { data, isLoading, isError, refetch } = useSavedReplies();
  const remove = useDeleteSavedReply();
  const { confirm, alert } = useDialog();
  const [editing, setEditing] = useState<SavedReply | null>(null);
  const [open, setOpen] = useState(false);

  function openEditor(reply: SavedReply | null) {
    setEditing(reply);
    setOpen(true);
  }

  async function handleDelete(reply: SavedReply) {
    const ok = await confirm({
      title: 'Delete saved reply',
      message: reply.isShared
        ? `Delete "${reply.name}"? It is shared, so it disappears for everybody in the office.`
        : `Delete "${reply.name}"?`,
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    remove.mutate(reply.id, {
      onError: (err) =>
        void alert({
          title: 'Could not delete',
          message: err.message || 'The reply could not be deleted.',
          variant: 'error',
        }),
    });
  }

  const columns: EntityListColumn<SavedReply>[] = [
    {
      key: 'name',
      header: 'Reply',
      render: (r) => (
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 font-medium text-gray-900">
            {r.name}
            {r.isShared && <Users size={13} className="text-gray-400" aria-label="Shared" />}
          </div>
          <div className="text-xs text-gray-500 truncate max-w-md">{r.body}</div>
        </div>
      ),
    },
    {
      key: 'owner',
      header: 'By',
      render: (r) => <span className="text-sm text-gray-600">{r.ownerName ?? '—'}</span>,
    },
    {
      key: 'actions',
      header: '',
      render: (r) =>
        r.canEdit ? (
          <div className="flex justify-end gap-1">
            <button
              type="button"
              onClick={() => openEditor(r)}
              aria-label={`Edit ${r.name}`}
              className="p-1.5 text-gray-500 rounded hover:bg-gray-100"
            >
              <Pencil size={14} />
            </button>
            <button
              type="button"
              onClick={() => void handleDelete(r)}
              aria-label={`Delete ${r.name}`}
              className="p-1.5 text-red-500 rounded hover:bg-red-50"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="bg-white rounded-lg shadow p-6" id="saved-replies">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h2 className="text-lg font-semibold text-gray-900">Saved replies</h2>
        <button
          type="button"
          onClick={() => openEditor(null)}
          className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700"
          data-testid="new-saved-reply"
        >
          <Plus size={14} />
          New reply
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Wording you insert from “Saved replies” in any email, conversation or guest message. Merge
        tags are filled from the event or vendor the message is about.
      </p>
      {isError ? (
        <div className="text-sm text-red-700 bg-red-50 rounded px-3 py-2">
          Your saved replies could not be loaded.{' '}
          <button type="button" className="underline" onClick={() => void refetch()}>
            Try again
          </button>
        </div>
      ) : (
        <EntityList
          items={data ?? []}
          columns={columns}
          getKey={(r) => r.id}
          isLoading={isLoading}
          emptyMessage="No saved replies yet."
        />
      )}
      <SavedReplyModal open={open} onClose={() => setOpen(false)} reply={editing} />
    </div>
  );
}
